import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCollection,
  useCollections,
  useCurrentUserID,
  useIsOwnSpace,
  useRecordsCount,
} from "@/api/hooks";
import {
  CollectionNode,
  buildCollectionTree,
  pathToCollection,
  resolveLevel,
} from "@/common/collectionTree";
import { HGCollection } from "@/common/types";
import CollectionCreate from "./CollectionCreate";
import CollectionRow from "./CollectionRow";
import PinnedCollections from "./PinnedCollections";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "../sidebar";
import { Input } from "../input";
import { Link, useParams } from "react-router";
import CollectionSettingsButton from "./CollectionSettingsButton";
import { ChevronLeft, Search } from "lucide-react";
import { collectionPath } from "@/common/urls";
import { cn } from "@/lib/utils";
import { consumePinnedNavigation } from "@/state/pinnedNavigation";
import { useCollectionPath } from "@/state/collectionsTree";
import { useShowAllRecords } from "@/common/allRecordsRange";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";

// Below this count, filtering isn't worth the extra input taking up space.
const SEARCH_THRESHOLD = 20;

// Header controls match the row targets: thumb-sized in the mobile sheet,
// tight against a pointer.
const ACTION = "size-9 md:size-7 static after:hidden";

/** A match in the search list: a row with no hierarchy around it. */
function flatNode(collection: HGCollection): CollectionNode {
  return {
    collection,
    implicit: false,
    key: collection.id,
    children: [],
    descendantCount: 0,
  };
}

export default function CollectionsList() {
  const { userID, collectionID } = useParams();
  const me = useAppSelector(selectMe);
  const collections = useCollections();
  const counts = useRecordsCount();
  // The URL carries a slug, so the active row can only be identified by the
  // resolved collection's real id.
  const activeCollectionID = useCollection()?.id;
  const canPin = useIsOwnSpace();
  // Clicking the already-open collection shows all of its records — widening
  // the time range to fit them, or flipping to the notes tab once the view is
  // already showing everything — instead of navigating to the page we are
  // already on.
  const showAllRecords = useShowAllRecords();
  // The sheet is the whole screen on mobile, so following a link from it has to
  // get out of the way of the page it just opened.
  const { isMobile, setOpenMobile } = useSidebar();
  // Where you are in the hierarchy is remembered per space, so browsing someone
  // else's collections leaves your own position alone.
  const space = useCurrentUserID() ?? "me";
  const { path, goTo } = useCollectionPath(space);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const activeItemRef = useRef<HTMLLIElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const revealedRef = useRef<string | undefined>(undefined);
  const pendingScrollRef = useRef(false);

  const roots = useMemo(() => buildCollectionTree(collections), [collections]);
  const { nodes, trail } = useMemo(
    () => resolveLevel(roots, path),
    [roots, path],
  );
  // Moves are made from the trail that actually resolved rather than from the
  // stored path, so a segment that has gone stale corrects itself on the way
  // rather than leaving "back" pointing at the level already on screen.
  const here = useMemo(() => trail.map((node) => node.collection.id), [trail]);
  const current = trail[trail.length - 1] as CollectionNode | undefined;
  const parent = trail[trail.length - 2] as CollectionNode | undefined;

  const trimmedQuery = query.trim().toLowerCase();
  const rows = useMemo(() => {
    if (!trimmedQuery) return nodes;
    // Search reaches across the whole space, so its results are shown flat:
    // a match is somewhere else in the hierarchy, and following it is what
    // moves the list there.
    return collections
      .filter((collection) =>
        collection.title.toLowerCase().includes(trimmedQuery),
      )
      .map(flatNode);
  }, [collections, nodes, trimmedQuery]);

  // Either listed at this level, or the collection this level belongs to.
  const activeIsOnScreen =
    !!activeCollectionID &&
    (current?.collection.id === activeCollectionID ||
      nodes.some((node) => node.collection.id === activeCollectionID));

  useEffect(() => {
    // Only one level is on screen, so the collection just navigated to is very
    // likely not on it. Keyed on a ref rather than on `path`: re-running would
    // drag the list back from wherever the user has since browsed to.
    if (!activeCollectionID || roots.length === 0) return;
    if (revealedRef.current === activeCollectionID) return;
    revealedRef.current = activeCollectionID;
    // Opening the collection whose children are showing must not walk back out
    // of it, and a row already on screen needs no revealing.
    if (activeIsOnScreen) return;
    // Otherwise the level that *lists* it — its ancestors — not the collection
    // itself, which would open onto its children and hide the row being
    // revealed.
    goTo(pathToCollection(roots, activeCollectionID));
  }, [activeCollectionID, activeIsOnScreen, roots, goTo]);

  useEffect(() => {
    // Only jump the scroll position when the selection came from clicking a
    // pinned collection — its match here is likely off-screen. Selecting
    // directly in this list shouldn't yank the scroll position around.
    if (consumePinnedNavigation()) pendingScrollRef.current = true;
  }, [userID, collectionID]);

  useEffect(() => {
    // Deliberately every render: the row cannot be scrolled to until the
    // reveal above has moved to its level and React has mounted it, which is
    // one or more commits after the navigation that asked for the scroll.
    if (!pendingScrollRef.current || !activeItemRef.current) return;
    pendingScrollRef.current = false;
    activeItemRef.current.scrollIntoView({ block: "end" });
  });

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const showSearch = collections.length > SEARCH_THRESHOLD;

  // Where a new collection should land: inside the open one when it is a row
  // the user can actually see, and otherwise inside the collection whose
  // children these rows are — so the new collection turns up on the level
  // being looked at rather than under whatever the URL happens to point at.
  // Null at the top level with nothing open there, which is a root collection.
  const createParent =
    rows.find((node) => node.collection.id === activeCollectionID)
      ?.collection ??
    current?.collection ??
    null;

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const handleToggleSearch = () => {
    if (searchOpen) closeSearch();
    else setSearchOpen(true);
  };

  /** A link in this list was followed: get out of its way. */
  const handleNavigate = () => {
    // A match is shown flat, so leaving the results is what lets the reveal
    // above put the collection back in its place in the hierarchy.
    if (trimmedQuery) closeSearch();
    if (isMobile) setOpenMobile(false);
  };

  const goUp = current ? () => goTo(here.slice(0, -1)) : undefined;

  // Signed out and in nobody's space, there is nothing to list and never will
  // be: GET /collections answers [] for a session-less request that names no
  // user. The header, the empty-state line and the create button would all be
  // furniture around a permanent void, so the panel stays blank until the
  // visitor picks someone from the switcher above it. (After all the hooks:
  // this is a decision about what to draw, not about what to compute.)
  if (!me && !userID) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">
      {canPin && (
        <PinnedCollections
          activeCollectionID={activeCollectionID}
          onActiveClick={showAllRecords}
          onNavigate={handleNavigate}
        />
      )}
      <SidebarGroup className="grow gap-1 overflow-auto p-0 pt-2">
        {/* The one place that says where in the hierarchy this level sits: the
            way back out, and the collection whose children are listed. */}
        <div className="flex shrink-0 items-center gap-1 px-2">
          {current ? (
            <>
              <button
                type="button"
                onClick={goUp}
                aria-label={
                  parent
                    ? `Back to ${parent.collection.title}`
                    : "Back to all collections"
                }
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-md text-muted-foreground",
                  "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  ACTION,
                )}
              >
                <ChevronLeft className="size-4" />
              </button>
              <Link
                to={collectionPath(current.collection)}
                aria-current={
                  activeCollectionID === current.collection.id
                    ? "page"
                    : undefined
                }
                onClick={(event) => {
                  if (activeCollectionID === current.collection.id) {
                    if (!showAllRecords) return;
                    event.preventDefault();
                    showAllRecords();
                    return;
                  }
                  handleNavigate();
                }}
                className={cn(
                  "min-w-0 flex-1 truncate text-[13px] font-semibold hover:underline",
                  activeCollectionID === current.collection.id
                    ? "text-primary"
                    : "text-sidebar-foreground",
                )}
              >
                {current.collection.title}
              </Link>
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate px-2 text-[10.5px] font-bold uppercase tracking-[.1em] text-sidebar-foreground/70">
              Collections
            </span>
          )}
          {showSearch && (
            <button
              type="button"
              aria-label={
                searchOpen ? "Hide collection search" : "Search collections"
              }
              aria-expanded={searchOpen}
              onClick={handleToggleSearch}
              className={cn(
                "flex shrink-0 items-center justify-center rounded-md text-sidebar-foreground",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                ACTION,
              )}
            >
              <Search className="size-4" />
            </button>
          )}
          <CollectionCreate className={ACTION} parent={createParent} />
        </div>
        {showSearch && searchOpen && (
          <div className="px-2 pb-0.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search collections..."
                className="h-9 pl-7 text-[13px] md:h-7"
              />
            </div>
          </div>
        )}
        <SidebarGroupContent className="h-full overflow-auto noscroll border-t border-sidebar-border">
          <SidebarMenu className="gap-0">
            {rows.length === 0 ? (
              <SidebarMenuItem className="px-3 py-2 text-[13px] text-muted-foreground">
                {trimmedQuery
                  ? "No matches"
                  : current
                    ? "Nothing inside"
                    : "No collections"}
              </SidebarMenuItem>
            ) : (
              rows.map((node) => {
                const isActive = activeCollectionID === node.collection.id;
                return (
                  <CollectionRow
                    key={node.key}
                    node={node}
                    isActive={isActive}
                    count={counts[node.collection.id]}
                    canPin={canPin}
                    onDrill={() => goTo([...here, node.collection.id])}
                    onNavigate={handleNavigate}
                    onUp={goUp}
                    onActiveClick={showAllRecords}
                    ref={isActive ? activeItemRef : undefined}
                  />
                );
              })
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <SidebarGroup className="gap-2">
        <CollectionSettingsButton />
      </SidebarGroup>
    </div>
  );
}
